import { useEffect, useSyncExternalStore } from "react";
import {
    addOperation as createOperation,
    deleteOperation as removeOperation,
    getPortfolio,
    updateOperation as saveOperation,
    type GetPortfolioResponse,
} from "features/investments/api/Portfolio";
import { subscribeApiSession } from "shared/api/apiClient";
import { responseCache } from "shared/money/responseCache";
import type { AddOperationPayload, Operation, PositionWithAsset, UpdateOperationPayload } from "types/Portfolio";
import { isOpenPortfolioPosition } from "shared/portfolio/positions";

export interface PortfolioSnapshot {
    operations: Operation[];
    positions: PositionWithAsset[];
    loading: boolean;
    error: string | null;
}

export interface PortfolioStore {
    subscribe: (listener: () => void) => () => void;
    subscribeOperations: (listener: () => void) => () => void;
    subscribeStatus: (listener: () => void) => () => void;
    getSnapshot: () => PortfolioSnapshot;
    getOperations: () => Operation[];
    getStatus: () => Pick<PortfolioSnapshot, "loading" | "error">;
    refresh: (force?: boolean) => Promise<void>;
    addOperation: (payload: AddOperationPayload) => Promise<{ success: boolean; error?: string }>;
    updateOperation: (id: string, payload: UpdateOperationPayload) => Promise<{ success: boolean; error?: string }>;
    deleteOperation: (id: string) => Promise<{ success: boolean; error?: string }>;
}

const emptySnapshot: PortfolioSnapshot = { operations: [], positions: [], loading: false, error: null };
const stores = new Map<string, PortfolioStore & { reset: () => void }>();

function normalize(response: unknown): Pick<PortfolioSnapshot, "operations" | "positions"> {
    const value = response && typeof response === "object" && !Array.isArray(response)
        ? response as Partial<GetPortfolioResponse>
        : {};
    return {
        operations: Array.isArray(value.operations) ? value.operations : [],
        positions: Array.isArray(value.positions) ? value.positions.filter(isOpenPortfolioPosition) as PositionWithAsset[] : [],
    };
}

function createStore(username: string, cacheUserId: string): PortfolioStore & { reset: () => void } {
    const endpoint = `/portfolio/${username}/portfolio`;
    const listeners = new Set<() => void>();
    const operationListeners = new Set<() => void>();
    const statusListeners = new Set<() => void>();
    const rawCached = responseCache.get<unknown>(endpoint, cacheUserId);
    const cached = rawCached && typeof rawCached === "object" && !Array.isArray(rawCached)
        ? rawCached as GetPortfolioResponse
        : null;
    let snapshot: PortfolioSnapshot = {
        ...emptySnapshot,
        ...normalize(cached),
        loading: !cached,
    };
    let statusSnapshot: Pick<PortfolioSnapshot, "loading" | "error"> = { loading: snapshot.loading, error: snapshot.error };
    let generation = 0;
    let request: Promise<void> | null = null;

    const publish = (next: PortfolioSnapshot) => {
        const operationsChanged = snapshot.operations !== next.operations;
        const statusChanged = statusSnapshot.loading !== next.loading || statusSnapshot.error !== next.error;
        snapshot = next;
        if (statusChanged) statusSnapshot = { loading: next.loading, error: next.error };
        listeners.forEach((listener) => listener());
        if (operationsChanged) operationListeners.forEach((listener) => listener());
        if (statusChanged) statusListeners.forEach((listener) => listener());
    };

    const refresh = (force = false): Promise<void> => {
        if (force) {
            generation += 1;
            request = null;
        }
        if (request) return request;
        const requestGeneration = generation;
        publish({ ...snapshot, loading: snapshot.operations.length === 0 && snapshot.positions.length === 0, error: null });
        const current = getPortfolio(username).then((response) => {
            if (generation !== requestGeneration) return;
            publish({ ...normalize(response), loading: false, error: null });
        }).catch((error: unknown) => {
            if (generation !== requestGeneration) return;
            publish({ ...snapshot, loading: false, error: String(error) });
        }).finally(() => {
            if (request === current) request = null;
        });
        request = current;
        return current;
    };

    const mutate = async (action: () => Promise<unknown>): Promise<{ success: boolean; error?: string }> => {
        try {
            await action();
            await refresh(true);
            return { success: true, ...(snapshot.error ? { error: snapshot.error } : {}) };
        } catch (error) {
            return { success: false, error: String(error) };
        }
    };

    return {
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        subscribeOperations: (listener) => {
            operationListeners.add(listener);
            return () => operationListeners.delete(listener);
        },
        subscribeStatus: (listener) => {
            statusListeners.add(listener);
            return () => statusListeners.delete(listener);
        },
        getSnapshot: () => snapshot,
        getOperations: () => snapshot.operations,
        getStatus: () => statusSnapshot,
        refresh,
        addOperation: (payload) => mutate(() => createOperation(username, payload)),
        updateOperation: (id, payload) => mutate(() => saveOperation(username, id, payload)),
        deleteOperation: (id) => mutate(() => removeOperation(username, id)),
        reset: () => {
            generation += 1;
            request = null;
            publish(emptySnapshot);
        },
    };
}

export function getPortfolioStore(username: string): PortfolioStore {
    const userId = responseCache.getCurrentUserId();
    const key = `${userId}:${username}`;
    let store = stores.get(key);
    if (!store) {
        store = createStore(username, userId);
        stores.set(key, store);
    }
    return store;
}

export function usePortfolioSnapshot(username: string | null): PortfolioSnapshot {
    const store = username ? getPortfolioStore(username) : null;
    const snapshot = useSyncExternalStore(
        store?.subscribe ?? (() => () => undefined),
        store?.getSnapshot ?? (() => emptySnapshot),
        () => emptySnapshot,
    );
    useEffect(() => {
        if (store) void store.refresh();
    }, [store]);
    return snapshot;
}

export function usePortfolioOperations(username: string | null): Pick<PortfolioSnapshot, "operations" | "loading" | "error"> {
    const store = username ? getPortfolioStore(username) : null;
    const operations = useSyncExternalStore(
        store?.subscribeOperations ?? (() => () => undefined),
        store?.getOperations ?? (() => emptySnapshot.operations),
        () => emptySnapshot.operations,
    );
    const status = useSyncExternalStore(
        store?.subscribeStatus ?? (() => () => undefined),
        store?.getStatus ?? (() => ({ loading: false, error: null })),
        () => ({ loading: false, error: null }),
    );
    useEffect(() => {
        if (store) void store.refresh();
    }, [store]);
    return { operations, ...status };
}

subscribeApiSession(() => {
    stores.forEach((store) => store.reset());
    stores.clear();
});
